/**
 * O modelo de ficha em arquivo — exportar e importar (trilha T5a).
 *
 * `vecchia.ficha-modelo`, versão 1: a rota de migração entre instalações e o
 * backup que a clínica leva embora. Importar valida o arquivo INTEIRO antes de
 * aceitar — modelo importado pela metade é ficha com campo faltando, e ninguém
 * sabe qual — e o resultado abre no editor sem salvar.
 */

import { PROTOCOLOS_DE_ADIPOMETRIA, escalaEncadeadaPorSlug, tabelaPorSlug } from './blocosDaFicha';
import { escalaPorSlug } from './escalasIlustradas';
import type { CampoDeFicha, FaixaDeClassificacao, ModeloDeFicha, TipoDeCampoDaFicha } from './tipos';

export const FORMATO_DO_MODELO = 'vecchia.ficha-modelo';
export const VERSAO_DO_FORMATO = '1.0';
/** O mesmo teto do protótipo. */
export const TAMANHO_MAXIMO_DO_ARQUIVO = 2 * 1024 * 1024;

/* Um `Record` e não uma lista: tipo novo em `TipoDeCampoDaFicha` que não entrar
   aqui é erro de compilação, e não arquivo recusado em silêncio. */
const TIPO_CONHECIDO: Record<TipoDeCampoDaFicha, true> = {
    texto: true, 'texto-longo': true, numero: true, data: true, 'selecao-unica': true, 'selecao-multipla': true,
    'texto-rico': true, tabela: true, arquivo: true,
    escala: true, 'tabela-selecionavel': true, 'escala-encadeada': true, 'calculo-imc': true, perimetria: true, adipometria: true,
};

function tipoConhecido(tipo: unknown): tipo is TipoDeCampoDaFicha {
    return typeof tipo === 'string' && Object.prototype.hasOwnProperty.call(TIPO_CONHECIDO, tipo);
}

export interface ModeloEmArquivo {
    formato: typeof FORMATO_DO_MODELO;
    versaoFormato: string;
    nome: string;
    exportadoEm: string;
    campos: CampoDeFicha[];
}

export type LeituraDoModelo =
    | { ok: true; nome: string; campos: CampoDeFicha[] }
    | { ok: false; erro: string };

export function modeloParaArquivo(modelo: ModeloDeFicha, agora: Date = new Date()): string {
    const documento: ModeloEmArquivo = {
        formato: FORMATO_DO_MODELO,
        versaoFormato: VERSAO_DO_FORMATO,
        nome: modelo.nome,
        exportadoEm: agora.toISOString(),
        campos: modelo.campos,
    };
    return JSON.stringify(documento, null, 2);
}

export function nomeDoArquivoDoModelo(nome: string): string {
    const slug = nome.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
        .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    return `${slug || 'modelo'}.vecchia.json`;
}

const ehTexto = (v: unknown): v is string => typeof v === 'string';
const ehListaDeTextos = (v: unknown): v is string[] => Array.isArray(v) && (v as unknown[]).every(ehTexto);

function ehFaixa(v: unknown): v is FaixaDeClassificacao {
    if (!v || typeof v !== 'object') return false;
    const faixa = v as Record<string, unknown>;
    return typeof faixa.ate === 'number' && ehTexto(faixa.rotulo);
}

/** Só as chaves que o campo conhece: o que vier a mais no arquivo não entra no modelo. */
function campoLimpo(bruto: Record<string, unknown>, tipo: TipoDeCampoDaFicha): CampoDeFicha {
    const campo: CampoDeFicha = { uid: String(bruto.uid), tipo, rotulo: ehTexto(bruto.rotulo) ? bruto.rotulo : '' };
    if (ehListaDeTextos(bruto.opcoes)) campo.opcoes = [...bruto.opcoes];
    if (typeof bruto.obrigatorio === 'boolean') campo.obrigatorio = bruto.obrigatorio;
    if (ehTexto(bruto.escala)) campo.escala = bruto.escala;
    if (ehTexto(bruto.tabela)) campo.tabela = bruto.tabela;
    if (ehListaDeTextos(bruto.colunas)) campo.colunas = [...bruto.colunas];
    if (ehListaDeTextos(bruto.linhas)) campo.linhas = [...bruto.linhas];
    if (bruto.sexo === 'feminino' || bruto.sexo === 'masculino' || bruto.sexo === '') campo.sexo = bruto.sexo;
    if (ehTexto(bruto.protocolo)) campo.protocolo = bruto.protocolo;
    if (Array.isArray(bruto.faixas)) {
        const faixas = bruto.faixas as unknown[];
        if (faixas.every(ehFaixa)) campo.faixas = faixas.map((f) => ({ ate: f.ate, rotulo: f.rotulo }));
    }
    return campo;
}

export function lerModeloDoArquivo(conteudo: string, nomeDoArquivo = ''): LeituraDoModelo {
    let documento: unknown;
    try {
        documento = JSON.parse(conteudo);
    } catch {
        return { ok: false, erro: 'O arquivo não é um JSON válido.' };
    }
    if (!documento || typeof documento !== 'object' || Array.isArray(documento)) return { ok: false, erro: 'O arquivo não é um JSON válido.' };

    const d = documento as Record<string, unknown>;
    if (d.formato !== FORMATO_DO_MODELO) return { ok: false, erro: 'Não é um modelo de ficha da Fórmula MED.' };
    if (String(d.versaoFormato ?? '').split('.')[0] !== VERSAO_DO_FORMATO.split('.')[0]) return { ok: false, erro: 'Formato incompatível com esta instalação.' };
    if (!Array.isArray(d.campos) || d.campos.length === 0) return { ok: false, erro: 'O modelo não tem nenhum campo.' };

    const brutos = d.campos as unknown[];
    if (brutos.some((b) => !b || typeof b !== 'object' || Array.isArray(b))) return { ok: false, erro: 'Há um campo mal formado no arquivo.' };
    const objetos = brutos as Record<string, unknown>[];

    const desconhecidos = [...new Set(objetos.filter((b) => !tipoConhecido(b.tipo)).map((b) => String(b.tipo)))];
    if (desconhecidos.length > 0) return { ok: false, erro: `Campos desconhecidos nesta instalação: ${desconhecidos.join(', ')}.` };

    const uids = objetos.map((b) => b.uid);
    if (uids.some((u) => !ehTexto(u) || !u.trim()) || new Set(uids).size !== uids.length) {
        return { ok: false, erro: 'Campo sem identificador, ou dois campos com o mesmo identificador.' };
    }

    const campos = objetos.map((b) => campoLimpo(b, b.tipo as TipoDeCampoDaFicha));
    const foraDoCatalogo = campos.filter((c) => (c.tipo === 'escala' && !escalaPorSlug(c.escala))
        || (c.tipo === 'escala-encadeada' && !escalaEncadeadaPorSlug(c.escala))
        || (c.tipo === 'tabela-selecionavel' && !tabelaPorSlug(c.tabela))
        || (c.tipo === 'adipometria' && c.protocolo !== undefined && !PROTOCOLOS_DE_ADIPOMETRIA.some((p) => p.slug === c.protocolo)));
    if (foraDoCatalogo.length > 0) {
        return { ok: false, erro: `Escala, tabela ou protocolo que esta instalação não conhece: ${foraDoCatalogo.map((c) => c.rotulo || c.uid).join(', ')}.` };
    }

    const nome = ehTexto(d.nome) && d.nome.trim()
        ? d.nome.trim()
        : nomeDoArquivo.replace(/\.vecchia\.json$/i, '').replace(/\.json$/i, '');
    return { ok: true, nome, campos };
}
